/**
 * RenewPassPopup — Home-screen modal that prompts the customer to renew a
 * Coffee Pass that's been fully used up / expired (Business Rule #23).
 *
 * Mirrors ReadyPickupPopup: self-gates on `GET /api/user/active-renewal-prompt`
 * (returns null when the customer holds an active OR queued pass, or has no
 * recently-ended pass / the plan is retired), and dismisses per-pass per-session
 * via sessionStorage so closing it doesn't immediately re-pop — a fresh session
 * surfaces it again until they renew. "Renew now" routes to the pass detail page
 * where the buy/customise flow lives.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Coffee } from 'lucide-react';
import { reportHomePopup } from '../utils/homePopups';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function dismissedKey(id) {
  return `pbc_renew_prompt_dismissed_${id}`;
}

export default function RenewPassPopup({ token }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [open, setOpen] = useState(false);

  // One pop-up per app open on Home (Business Rule #64) — the launch pop-up stands aside.
  useEffect(() => {
    reportHomePopup('renew_pass', open);
    return () => reportHomePopup('renew_pass', false);
  }, [open]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/user/active-renewal-prompt`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const p = resp.data;
        if (!p || !p.subscription_id) { setPrompt(null); setOpen(false); return; }
        const key = dismissedKey(p.user_sub_id || p.subscription_id);
        if (sessionStorage.getItem(key)) { setPrompt(p); setOpen(false); return; }
        setPrompt(p); setOpen(true);
      } catch (_) { /* non-essential surface */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleClose = (next) => {
    setOpen(next);
    if (!next && prompt) {
      sessionStorage.setItem(dismissedKey(prompt.user_sub_id || prompt.subscription_id), '1');
    }
  };

  if (!prompt) return null;

  const total = prompt.redemptions_total || 0;
  const windowDays = prompt.redemption_window_days || 0;
  const price = Math.round(prompt.price || 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm overflow-hidden gap-0 border-primary/15 p-0" data-testid="renew-pass-popup">
        <DialogTitle className="sr-only">Renew your Coffee Pass</DialogTitle>
        <DialogDescription className="sr-only">Your pass is used up — renew to keep going.</DialogDescription>

        <div className="px-6 pt-7 pb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: 'hsl(25 100% 95%)' }}>
            <Coffee className="h-8 w-8" style={{ color: 'hsl(var(--coffee))' }} strokeWidth={1.5} />
          </div>
          <h3 className="font-fraunces text-xl font-semibold leading-tight">Your {prompt.name} is all used up</h3>
          <p className="mx-auto mt-1.5 mb-4 max-w-[260px] text-sm text-muted-foreground">
            Loved it? Renew now and pick up right where you left off.
          </p>

          <div className="mb-4 grid grid-cols-3 overflow-hidden rounded-2xl border border-[hsl(30_14%_91%)] bg-[hsl(30_14%_97%)]">
            <PopStat v={total} c="Drinks" />
            <PopStat v={windowDays} c="Days" divider />
            <PopStat v={<><span className="text-xs">₹</span>{price}</>} c="Price" divider />
          </div>

          <Button
            className="w-full rounded-full text-base font-semibold"
            onClick={() => { handleClose(false); navigate(`/passes/${prompt.subscription_id}`); }}
            data-testid="renew-popup-cta"
          >
            Renew now — ₹{price}
          </Button>
          <Button variant="ghost" className="mt-1.5 w-full rounded-full" onClick={() => handleClose(false)} data-testid="renew-popup-later">
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PopStat({ v, c, divider }) {
  return (
    <div className={`px-2 py-2.5 ${divider ? 'border-l border-[hsl(30_12%_89%)]' : ''}`}>
      <div className="font-fraunces text-base font-bold leading-none">{v}</div>
      <div className="mt-1.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{c}</div>
    </div>
  );
}
